import { DeskCheckBoxProps, DeskCheckBoxComponents } from '@my-types';
import { React, Styled } from '@common/3rd';
import { StyledComponents } from '@styles';
import { ModelWidget, DeskComponentRegistry } from '@component';

/**
 * 由于组件第一次构建的时候, model可能还没有值, 导致使用defaultChecked属性是false或者undefined.
 * 之后model值构建完成之后, 更新defaultChecked不能产生效果, 导致第一次onChange不能够正确响应.
 * 因此, 改用didMount和didUpdate之后直接dom设置checked值来替代defaultChecked属性.
 */
class DeskCheckBox<
	P extends DeskCheckBoxProps,
	S,
	C extends DeskCheckBoxComponents
> extends ModelWidget<P, S, C> {
	checkboxRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.checkboxRef = React.createRef();
	}
	componentDidMount() {
		super.componentDidMount();
		this.checkboxRef.current.checked = !!this.getValueFromModel();
	}
	componentDidUpdate(prevProps: any, prevState?: any, prevContext?: any) {
		super.componentDidUpdate(prevProps, prevState, prevContext);
		this.checkboxRef.current.checked = !!this.getValueFromModel();
	}
	protected initComponents(): C {
		return {
			Box: Styled(StyledComponents.CHECKBOX).attrs({
				'data-widget': 'desk-checkbox'
			})`
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box
				onChange={this.onValueChanged}
				onFocus={this.onInputFocus}
				onBlur={this.onInputBlur}
				disabled={!this.isEditable()}
				ref={this.checkboxRef}
				id={this.getId()}
			/>
		);
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.checkboxRef.current.checked = value ? true : false;
		}
		return this;
	}
	protected isEditable(): boolean {
		const props = this.props as any;
		return !(props.readonly || props.disabled);
	}
	private onValueChanged = (): void => {
		const value = this.checkboxRef.current.checked;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value);
	};
	private onInputFocus = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	private onInputBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
	protected getId(): string {
		return this.props.id as string || '';
	}
	focus(): this {
		this.checkboxRef.current.focus();
		return this;
	}
}

DeskComponentRegistry.register({
	checkbox: DeskCheckBox
});

export { DeskCheckBox };
